// ---- Cinematic letterbox + dialogue HUD (plan 26) ----
// The film chrome: top/bottom black bars that slide in, a speaker-named
// dialogue line that streams onto the bottom bar (the pure timeline decides
// WHAT is visible — this layer only paints), a "Skip ▸" affordance when the doc
// allows it, and a full-screen click-catcher so a mid-scene click advances the
// beat instead of leaking into the world (canvas focus/pickup handlers never
// see it). Identical in the game and the studio — plain DOM, no game imports.
//
// z-order: bars 15 + catcher 14 sit ABOVE the gameplay HUD (≤11) and BELOW
// #fade(19)/#loading(20)/#displaced(30), so world-swap fades cover the bars.
// While mounted, `body.cine` is set — index.html hides the HUD off that class.

import type { CineLine } from "./timeline.ts";

export type CineUi = {
  setLine(line: CineLine | null, nameOf: (actorId: string) => string): void;
  animateIn(): void;
  /** Slide the bars out; resolves when the transition lands. */
  animateOut(): Promise<void>;
  dispose(): void;
};

export type CineUiOpts = {
  skippable: boolean;
  /** Left-click anywhere: complete the streaming line / advance the beat. */
  onAdvance(): void;
  /** The Skip ▸ affordance (only rendered when skippable). */
  onSkip(): void;
};

const BAR_H = "12vh";
const SLIDE_MS = 400;

export function mountCineUi(opts: CineUiOpts): CineUi {
  const root = document.createElement("div");
  root.id = "cine";

  const bar = (edge: "top" | "bottom"): HTMLDivElement => {
    const el = document.createElement("div");
    el.style.cssText =
      `position:fixed;left:0;right:0;${edge}:0;height:${BAR_H};background:#000;z-index:15;` +
      `transform:translateY(${edge === "top" ? "-100%" : "100%"});transition:transform ${SLIDE_MS}ms ease;` +
      `display:flex;align-items:center;justify-content:center;pointer-events:none;`;
    return el;
  };
  const top = bar("top");
  const bottom = bar("bottom");

  // Bottom-bar dialogue block: speaker name (small, gold) over the streaming line.
  const lineWrap = document.createElement("div");
  lineWrap.style.cssText = "max-width:min(860px,86vw);text-align:center;opacity:0;transition:opacity 150ms ease;";
  const speaker = document.createElement("div");
  speaker.style.cssText =
    "font:600 12px/1.4 system-ui,sans-serif;letter-spacing:0.14em;text-transform:uppercase;color:#d9b96c;margin-bottom:4px;";
  const text = document.createElement("div");
  text.style.cssText = "font:16px/1.5 system-ui,sans-serif;color:#f2ecdc;text-shadow:0 1px 2px #000;min-height:1.5em;";
  const hint = document.createElement("span");
  hint.textContent = " ▸";
  hint.style.cssText = "color:#d9b96c;opacity:0;transition:opacity 200ms ease;";
  lineWrap.append(speaker, text);
  text.appendChild(hint);
  bottom.appendChild(lineWrap);

  // Skip affordance on the top bar (doc-gated).
  let skipEl: HTMLDivElement | null = null;
  if (opts.skippable) {
    skipEl = document.createElement("div");
    skipEl.textContent = "Skip ▸";
    skipEl.style.cssText =
      "position:absolute;right:18px;top:50%;transform:translateY(-50%);font:12px system-ui,sans-serif;" +
      "color:#d9b96c99;cursor:pointer;pointer-events:auto;padding:6px 8px;";
    skipEl.onmouseenter = () => { skipEl!.style.color = "#d9b96c"; };
    skipEl.onmouseleave = () => { skipEl!.style.color = "#d9b96c99"; };
    skipEl.onpointerdown = (e) => {
      e.stopPropagation();
      opts.onSkip();
    };
    top.appendChild(skipEl);
  }

  // Full-screen click catcher — swallows pointerdown so world handlers
  // (focus/pickup on the canvas) never fire during a cinematic.
  const catcher = document.createElement("div");
  catcher.style.cssText = "position:fixed;inset:0;z-index:14;cursor:pointer;";
  catcher.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.button === 0) opts.onAdvance();
  });

  root.append(catcher, top, bottom);
  document.body.appendChild(root);
  document.body.classList.add("cine");

  let lastKey = "";
  let lastVisible = -1;
  return {
    setLine(line, nameOf) {
      if (!line) {
        lineWrap.style.opacity = "0";
        lastKey = "";
        lastVisible = -1;
        return;
      }
      const key = `${line.actor}|${line.text}`;
      if (key !== lastKey) {
        lastKey = key;
        lastVisible = -1;
        speaker.textContent = nameOf(line.actor);
        lineWrap.style.opacity = "1";
      }
      // Repaint only when the streamed slice changed — this runs every frame,
      // but characters land at ~28/s and a held line is static for seconds.
      if (line.visible !== lastVisible) {
        lastVisible = line.visible;
        text.textContent = line.text.slice(0, line.visible);
        text.appendChild(hint); // keep the advance hint as the last child
        hint.style.opacity = line.complete ? "1" : "0";
      }
    },
    animateIn() {
      requestAnimationFrame(() => {
        top.style.transform = "translateY(0)";
        bottom.style.transform = "translateY(0)";
      });
    },
    animateOut() {
      top.style.transform = "translateY(-100%)";
      bottom.style.transform = "translateY(100%)";
      lineWrap.style.opacity = "0";
      return new Promise((resolve) => setTimeout(resolve, SLIDE_MS + 60));
    },
    dispose() {
      root.remove();
      document.body.classList.remove("cine");
    },
  };
}
